import { Button, CircularProgress, DialogActions, DialogContentText, Divider, List, ListItem, Typography } from '@material-ui/core';
import { Dispatch } from '@reduxjs/toolkit';
import React, { Component, Fragment } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { AppDispatch, AppState } from 'store';
import { dialogClose } from 'store/dialog/dialog.actions';
import { DialogOpenPayloadJobs } from 'store/dialog/dialog.interface';
import { performOperation } from 'store/jobs/jobs.actions';
import { getJobActionSpinnerSelector } from 'store/spinner/spinner.selectors';
import { ErrorMessageDetailsDTO, MassOperationRequest } from 'utils/restApplicationClient';
import { formatErrorMessageWithParams } from 'utils/formatErrorMessage';
import { CustomDialogButtonContainer, CustomDialogContent } from '../CustomDialog.styled';
import { DialogVariantEnum } from "enums/dialogVariant";

interface IProps {
  jobs: DialogOpenPayloadJobs;
  variant: DialogVariantEnum.WORKFLOW | DialogVariantEnum.PARTIAL_WORKFLOW | DialogVariantEnum.DOWNLOAD_UNSUCCESSFUL | DialogVariantEnum.VALIDATION_ERROR;
  errorMessagesDetails?: ErrorMessageDetailsDTO[];
}

interface IDispatchProps {
  performOperation: (payload: MassOperationRequest) => AppDispatch;
  dialogClose: () => AppDispatch;
}

interface IStateProps {
  jobActionSpinner: boolean;
}

type PropType = IDispatchProps & WithTranslation & IProps & IStateProps;

class ProjectCancelled extends Component<PropType> {
  handleClose = (): void => {
    const { dialogClose } = this.props;

    dialogClose();
  };

  handleImportClick = (): void => {
    const { performOperation, jobs } = this.props;
    performOperation({
      jobsIds: jobs.map(({ id }) => id),
      operation: 'IMPORT',
    });
  };

  handleRemoveClick = (): void => {
    const { performOperation, jobs } = this.props;
    performOperation({
      jobsIds: jobs.map(({ id }) => id),
      operation: 'CANCEL',
    });
  };

  getText(): string {
    const { variant } = this.props;
    switch (variant) {
      case DialogVariantEnum.PARTIAL_WORKFLOW:
        return 'modal.stepPartiallyUnmodifiable';
      case DialogVariantEnum.VALIDATION_ERROR:
        return 'modal.validationErrorDescription';
      case DialogVariantEnum.DOWNLOAD_UNSUCCESSFUL:
        return 'modal.downloadUnsuccessful';
      case DialogVariantEnum.WORKFLOW:
      default:
        return 'modal.stepUnmodifiable';
    }
  }

  isJobDeleteDisabled(): boolean {
    const { jobs } = this.props;
    return !jobs.some((job) => job.status && ['NEW', 'IN_PROGRESS', 'SENT'].includes(job.status))
  }

  render(): JSX.Element {
    const { t, jobActionSpinner, errorMessagesDetails, variant } = this.props;
    const isDisabled = jobActionSpinner;

    return (
      <Fragment>
        <CustomDialogContent>
          <DialogContentText>
            <Typography component="span" color="textPrimary">
              {t(this.getText())}
            </Typography>
          </DialogContentText>
          {variant === DialogVariantEnum.VALIDATION_ERROR && errorMessagesDetails && errorMessagesDetails.length > 0 && (
            <List dense disablePadding>
              {errorMessagesDetails.map((detail, index) => (
                <ListItem key={index} disableGutters>
                  <Typography color="textPrimary" component="span" style={{ whiteSpace: 'pre-line' }}>
                    {formatErrorMessageWithParams(detail.errorMessage, detail.params)}
                  </Typography>
                </ListItem>
              ))}
            </List>
          )}
        </CustomDialogContent>
        <Divider />
        <DialogActions>
          <CustomDialogButtonContainer>
            <Button color="secondary" disabled={isDisabled} variant="contained" onClick={this.handleImportClick}>
              {(jobActionSpinner && <CircularProgress size="24px" color="secondary" />) || t('modal.importAgain')}
            </Button>
            <Button variant="outlined" disabled={isDisabled || this.isJobDeleteDisabled()} onClick={this.handleRemoveClick}>
              {(jobActionSpinner && <CircularProgress size="24px" />) || t('modal.delete')}
            </Button>
            <Button variant="outlined" disabled={isDisabled} onClick={this.handleClose}>
              {t('common.cancel')}
            </Button>
          </CustomDialogButtonContainer>
        </DialogActions>
      </Fragment>
    );
  }
}

const mapStateToProps = (state: AppState): IStateProps => ({
  jobActionSpinner: getJobActionSpinnerSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch<AppDispatch>): IDispatchProps => ({
  performOperation: (payload: MassOperationRequest): AppDispatch => dispatch(performOperation(payload)),
  dialogClose: (): AppDispatch => dispatch(dialogClose()),
});

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(ProjectCancelled));
